import Config from '../config'
import OpeOrderItem from '../objects/opeorderitem.js'
import OpeImageAmazon from '../objects/opeimage/amazon.js'
import OpeImagePhotoorganiser from '../objects/opeimage/photoorganiser.js'
import ProductStore from '../stores/product-store'
import OrderStore from '../stores/order-store'
import ImageStore from '../stores/image-store'
import UploadActions from '../actions/upload-actions'
import SelectorActions from '../actions/selector-actions'
import ProductCalculations from '../utils/product-calculations'
import AdobeAnalytics from '../utils/adobe-analytics'
import Ajax from '../utils/ajax'



var SaveApi = {};

/**
 * Load items into editor from an object describing all the items.
 * The object is usually a parsed JSON that has been created by the editor-output-creator.js,
 * and saved somewhere and then loaded into this function later. Kind of like loading av saved project.
 * @param  {Object} editorData
 */
SaveApi.loadItems = function (editorData) {
	if (typeof editorData !== 'object') {
		displayError("Cannot load editorData because given parameter editorData is not an object", {editorData: editorData});
		return;
	}
	if (!editorData.hasOwnProperty("product")) {
		displayError("Cannot load editorData because given parameter editorData does not contain a product prop.", {editorData: editorData});
		return;
	}

	for (var i = 0; i < editorData.product.images.length; i++) {
		var item = editorData.product.images[i];
		var loadData = JSON.parse(decodeURIComponent(escape(atob(item.loadData))));

		var imageId = SaveApi._makeId(16);
		var productId = item.product;
		var options = loadData.itemOptions;
		var isPortrait = loadData.isPortrait;

		//Create OpeImage object
		if (item.imageSource === "AMAZON") {
			var opeImage = new OpeImageAmazon(imageId, item.imageId, loadData.imageWidth, loadData.imageHeight);
		} else {
			throw new Error("Cannot create opeImage because it has an image source other than AMAZON. ImageSource: " + item.imageSource);
		}

		//Add image to ImageStore
        UploadActions.addImageToStore(opeImage);

        //Create OpeItem
		var opeItem = new OpeOrderItem(imageId, productId, options, isPortrait);
		opeItem.setProductHeight(loadData.productHeight);
		opeItem.setProductWidth(loadData.productWidth);
		opeItem.setImageTakenDate(item.dateTaken);

        //Add item to order store
        SelectorActions.addOrderItem(opeItem);
	}

}

/**
 * This will load images from the basket in Photo Organiser.
 * It will not return the images, but put them directly into the image
 * and order store.
 *
 * Before using this method one needs to be logged in and have an
 * access_token in sessionStorage.getItem("photoOrganiseLoginToken")
 * @return {Promise} [description]
 */
SaveApi.loadFromPhotoorganiser = function () {
    return new Promise(function (resolve, reject) {
        var limit = 250;
        var totalBasketPhotoCount = null;

        //First load images to see how many basket images there is in total (the number is included in the resultset, but the number of photos returned can be less)
        SaveApi._loadFromPOPaginated(0, limit).then(function (result) {

            totalBasketPhotoCount = result.MatchedPhotoCount;
            if (result.Photos.length < totalBasketPhotoCount) { //There are still more images to load, load them all in parallell because thats faster than loading them in serial
                var promises = [];
                var currentSkip = 0;
                //How many requests do we need to get all the remaining photos given that we can only load "limit" photos on each request?
                var parallellRequests = Math.ceil((totalBasketPhotoCount-limit)/limit);

                //Create a bunch of requests to be run in parallell to fetch the photos
                for (var i = 0; i < parallellRequests; i++) {
                    currentSkip += limit;
                    promises.push(SaveApi._loadFromPOPaginated(currentSkip, limit));
                }

                //Run all the requests
                Promise.all(promises).then(function () {
                    AdobeAnalytics.photosImportedFromPhotoOrganiser(totalBasketPhotoCount);
                    OrderStore.sortItemsByImageTakenDate();
                    resolve();
                }).catch(function (error) {
                    reject(error);
                });
            } else {
                AdobeAnalytics.photosImportedFromPhotoOrganiser(totalBasketPhotoCount);
                resolve();
            }
        });

    });
}

/**
 * The TemporarySelectionApi is different from the PO Basket. Although they are
 * similar and is used for much of the same things. The selection api holds a selection
 * of image IDs, and their source, such as PhotoOrganiser. We will fetch the IDs, then
 * ask PO to enrich the data and then add the images to the editor
 */
SaveApi.loadFromTemporarySelectionApi = function (temporarySelectionId) {
	return new Promise(function (resolve, reject) {
		var url = Config.TemporarySelectionApi + "/api/v1/temporaryselections/" + temporarySelectionId;
		//Get photos
		Ajax.get({
				url: url,
				responseType: 'JSON',
				headers: {
						'accept': 'application/json',
						'Authorization': 'Bearer ' + sessionStorage.getItem("photoOrganiseLoginToken")
				}
		}).then(function (result) {
				SaveApi._enrichImages(result.images).then(function (enrichedImages) {
					SaveApi._addPhotos(enrichedImages);
					resolve();
				});
		})['catch'](function (error) {
				if (error.status == 404) {
						console.error("Got a 404 from temporary selection API")
						resolve();
				} else {
						reject({message: _("Unable to load products from teporary selection API in Photo Organise"), error: error, url: url});
				}
		});
	});
}

/***
 * Load images based on image ids
 */
SaveApi.loadImageList = function (imageIdList) {

    SaveApi._fetchPhotoData(imageIdList).then(function (imageList) {
        SaveApi._addPhotos(imageList)
    }).catch(function (error) {
        throw new Error(error)
    })

}

/**
 * The imageList we get from the TemporarySelectionApi only have imageIds and image source
 * This function will ask the PO API for more data on each image, thus "Enriching" the data we have
 */
SaveApi._enrichImages = function (imageList) {

    //Take the imagelist and change it a bit so that it can be accepted at the PhotoOrganiser Query API
    var photoOrganiseImageList = [];
    for (var i = 0; i < imageList.length; i++) {
        if (imageList[i].source == "PhotoOrganiser") {
            photoOrganiseImageList.push(imageList[i].imageId);
        } else {
            throw new Error("Tried to enrich images fetched from TemporarySelectionApi, but the image has a different source than expected. The only implemented source is PhotoOrganiser, but found an image with a source: " + imageList[i].source);
        }
    }

    return SaveApi._fetchPhotoData(photoOrganiseImageList);
}

SaveApi._fetchPhotoData = function (photoIdList) {
    return new Promise(function (resolve, reject) {

		//Send the list of image IDs to the Photo Organiser Query API. This will return the images with enriched data, such as height, width, name, etc.
		var url = Config.PhotoOrganiseQueryApi + "/api/v1/photos/";
		Ajax.post({
				url: url,
				body: photoIdList,
				responseType: 'JSON',
				headers: {
						'accept': 'application/json',
						'Authorization': 'Bearer ' + sessionStorage.getItem("photoOrganiseLoginToken")
				}
		}).then(function (result) {
				resolve(result);
		})['catch'](function (error) {
				if (error.status == 404) {
						console.error("Got a 404 from query api when trying to enrich images that we have gotten from the temporary selection api", imageList);
						resolve();
				} else {
						reject({message: _("Unable to load images from temporary selection API in Photo Organise"), error: error, url: url});
				}
		});
	});
}

SaveApi._addPhotos = function (photos) {
    OrderStore.startTransaction();
    ImageStore.startTransaction();

    for (var i = 0; i < photos.length; i++) {

        var data = photos[i];

        if (data.VisibleWidth == 0 || data.VisibleHeight == 0) {
            var size = SaveApi._getImageWidthAndHeightInAVeryHackyWay(data);
            data.VisibleHeight = size.height;
            data.VisibleWidth = size.width;
        }

        var image = new OpeImagePhotoorganiser(data.ImageId, data.Thumbnails, data.VisibleWidth, data.VisibleHeight, (new Date(data.Date).getTime() / 1000));

        //Add image to ImageStore
        UploadActions.addImageToStore(image);

        //Create order item
        var orderItem = new OpeOrderItem(data.ImageId, null, {
            quantity: 1, position: {"x" : 0, "y" : 0, "percentageX" : 0, "percentageY" : 0 },
            scale: 1.0, filter: false, filename: data.Name, imageFilter: {} });


        //Calculate which product will suit the image best considering the dimensions of the image
        var selectedProduct = ProductCalculations.autoSelectProduct(image.getWidth(), image.getHeight(), sessionStorage.getItem("autoSelectProduct"), sessionStorage.getItem("autoSelectProductWidth"));
				orderItem.setProductId(selectedProduct.productId);
        orderItem.setProductWidth(selectedProduct.productWidth);
        orderItem.setProductHeight(selectedProduct.productHeight);

        // Check the ratio difference of the product selected
        if(selectedProduct.matchRatioDiff > Config.ProductRatioWarning) {
            orderItem.setAutoCropWarning(true);
        }
        // Check DPI of uploaded image
        var imageProductDPI = ProductCalculations.getDPI(selectedProduct, image.getWidth(), image.getHeight());
        // @TODO the 300 value should be checked from a config file
        if(imageProductDPI < 300) {
           orderItem.setLowResWarning(true);
        }

        //Figure out if the image is portrait or landscape mode
        var fileIsPortrait = ProductCalculations.isPortrait(image.getWidth(), image.getHeight(), 0, null);

        orderItem.isPortrait = fileIsPortrait;

        var date = Date.parse(data.Date);
        if (date) {
            //Set the date of when the image was taken (if available)
            orderItem.setImageTakenDate(date/1000);
        }

        SelectorActions.addOrderItem(orderItem);

    }
    OrderStore.stopTransaction();
    ImageStore.stopTransaction();
}

SaveApi._loadFromPOPaginated = function (skip, limit) {
    return new Promise(function (resolve, reject) {

        //Get photos
        Ajax.get({
            url: Config.PhotoOrganiseQueryApi + '/api/v1/photos/GetImageIdsForTemporarilyphotoselectionStripped/?Skip=' + skip + '&Limit=' + limit + '&SortingField=PhotoDate',
            responseType: 'JSON',
            headers: {
                'accept': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem("photoOrganiseLoginToken")
            }
        }).then(function (result) {
            SaveApi._addPhotos(result.Photos);
            resolve(result);
        })['catch'](function (error) {
            if (error.status == 404) {
                alert(_("Could not load photos from Photo Organiser because there are no photos in the basket."));
                resolve();
            } else {
                reject({message: _("Unable to load products from basket in Photo Organise"), error: error, url: url});
            }
        });
    });
}

/**
 * Create a random string with given length
 * @param  {int}
 * @return {string}
 */
SaveApi._makeId = function (size) {
    var text = "";
    var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for( var i=0; i < size; i++ )
        text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
}

//This is used to get a fake image height and width based on one of the thumbs
//It gives the width and height in the same ratio but always a fixed
//height of 2500. This is because PO Api sometimes does not give width and height
//and the boss wants it released quickly
SaveApi._getImageWidthAndHeightInAVeryHackyWay = function (imageData) {
    var thumbHeight = imageData.Thumbnails[0].Height;
    var thumbWidth = imageData.Thumbnails[0].Width;

    var ratio = thumbHeight/thumbWidth;
    return {width: Math.round(2500/ratio), height: 2500};
}

export default SaveApi;
